import { getFirestore, collection, query, where, getDocs, limit, orderBy } from "firebase/firestore";
import { app } from "../../firebase-config.js";

const db = getFirestore(app);

export class SeriesPickerModal {
    constructor(onSelect) {
        this.onSelect = onSelect;
        this.overlay = null;
        this.modal = null;
    }

    render() {
        // Remove existing if any
        const existing = document.getElementById('series-picker-modal');
        if (existing) existing.remove();

        // Create Modal Elements
        this.overlay = document.createElement('div');
        this.overlay.id = 'series-picker-modal';
        this.overlay.className = 'fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center animate-fade-in backdrop-blur-sm';

        this.overlay.innerHTML = `
            <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 flex flex-col max-h-[80vh] overflow-hidden transform transition-all scale-95 opacity-0" id="picker-content">
                <!-- Header -->
                <div class="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <h3 class="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <span class="material-symbols-outlined text-indigo-600">search</span>
                        작품 선택
                    </h3>
                    <button id="btn-picker-close" class="text-gray-400 hover:text-gray-600 transition-colors">
                        <span class="material-symbols-outlined">close</span>
                    </button>
                </div>

                <!-- Search Input -->
                <div class="p-4 border-b border-gray-100">
                    <div class="relative">
                        <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 material-symbols-outlined text-sm">search</span>
                        <input type="text" id="picker-search-input" class="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow" placeholder="작품 제목 검색..." autofocus>
                    </div>
                </div>

                <!-- Results List -->
                <div id="picker-results" class="flex-1 overflow-y-auto p-2 space-y-1 min-h-[300px] bg-gray-50/50">
                    <div class="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <span class="material-symbols-outlined text-3xl opacity-20">library_books</span>
                        <p class="text-xs">검색어를 입력하세요</p>
                    </div>
                </div>
            </div>
        `;

        document.body.appendChild(this.overlay);

        // Animate In
        requestAnimationFrame(() => {
            const content = this.overlay.querySelector('#picker-content');
            content.classList.remove('scale-95', 'opacity-0');
            content.classList.add('scale-100', 'opacity-100');
        });

        // Event Listeners
        const closeBtn = this.overlay.querySelector('#btn-picker-close');
        const input = this.overlay.querySelector('#picker-search-input');

        closeBtn.onclick = () => this.close();
        this.overlay.onclick = (e) => {
            if (e.target === this.overlay) this.close();
        };

        // Debounced Search
        let debounceTimer;
        input.oninput = (e) => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => this.search(e.target.value.trim()), 300);
        };

        input.focus();
    }

    async search(keyword) {
        const resultContainer = this.overlay.querySelector('#picker-results');

        if (!keyword) {
            resultContainer.innerHTML = `
                <div class="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                    <span class="material-symbols-outlined text-3xl opacity-20">library_books</span>
                    <p class="text-xs">검색어를 입력하세요</p>
                </div>
            `;
            return;
        }

        resultContainer.innerHTML = '<div class="flex justify-center py-10"><span class="loader-spin w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></span></div>';

        try {
            // Simple prefix search simulation 
            // Note: Firestore doesn't support full-text search directly without 3rd party.
            // We use 'array-contains' if keywords exist, or simple >= string comparison for titles.
            // Using title range query for 'starts with' behavior.
            const q = query(
                collection(db, 'series'),
                where('title', '>=', keyword),
                where('title', '<=', keyword + '\uf8ff'),
                limit(10)
            );

            const snapshot = await getDocs(q);

            if (snapshot.empty) {
                resultContainer.innerHTML = `
                    <div class="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <span class="material-symbols-outlined text-3xl opacity-20">search_off</span>
                        <p class="text-xs">검색 결과가 없습니다.</p>
                    </div>
                `;
                return;
            }

            resultContainer.innerHTML = '';
            snapshot.forEach(doc => {
                const data = doc.data();
                const item = document.createElement('div');
                item.className = 'flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100 hover:border-indigo-300 hover:shadow-sm cursor-pointer transition-all group';
                item.innerHTML = `
                    <img src="${data.thumbnail || data.coverImage || 'https://via.placeholder.com/100'}" class="w-10 h-10 rounded object-cover bg-gray-200">
                    <div class="flex-1 min-w-0">
                        <h4 class="text-sm font-bold text-gray-900 truncate group-hover:text-indigo-600 transition-colors">${data.title}</h4>
                        <p class="text-xs text-gray-500 truncate">${data.authorName} • IDs: ${doc.id}</p>
                    </div>
                    <button class="text-xs font-bold bg-gray-100 text-gray-600 px-3 py-1.5 rounded-full group-hover:bg-indigo-600 group-hover:text-white transition-colors">선택</button>
                `;
                item.onclick = () => {
                    this.onSelect({ id: doc.id, ...data });
                    this.close();
                };
                resultContainer.appendChild(item);
            });

        } catch (e) {
            console.error(e);
            resultContainer.innerHTML = '<div class="text-center text-red-500 py-4 text-sm">Search Error</div>';
        }
    }

    close() {
        if (this.overlay) {
            this.overlay.classList.remove('animate-fade-in');
            this.overlay.querySelector('#picker-content').classList.remove('scale-100', 'opacity-100');
            this.overlay.querySelector('#picker-content').classList.add('scale-95', 'opacity-0');

            setTimeout(() => {
                this.overlay.remove();
                this.overlay = null;
            }, 200);
        }
    }
}
